import { PEOPLE_ORIGIN } from "./origins";
import { PUBLISHER } from "./ppl-schema";

/** Where the film's files are served from: routes/media.$file.ts today (the
 *  files live in public/film/). Moving them to a bucket or a CDN is this one line. */
export const MEDIA_BASE = "/media";

/** The films still draw the agents as they were before their planets (2026-10-25), so they stay
 *  off the site until re-cut. On, every film block, button, link, slide and video tag returns. */
export const FILMS_ON = false;
// The files in public/film/ live in git history (the commit before their removal) and must be
// restored before this turns on.

interface FilmSource {
  src: string;
  type: string;
  media?: string;
}

export interface Film {
  title: string;
  description: string;
  /** As the button reads it. */
  length: string;
  seconds: number;
  isoDuration: string;
  uploadDate: string;
  width: number;
  height: number;
  poster: string;
  sources: readonly [FilmSource, ...FilmSource[]];
  captions?: string;
}

/* Its captions are burned into the picture, so it carries no caption track. */
export const FILM = {
  title: "PPL AI Workforce and Operating System",
  description:
    "The PEOPLE AI Workforce brand film: AI employees at every desk of an assisted-living community, and the operating system they run on.",
  length: "1:33",
  seconds: 93,
  isoDuration: "PT1M33S",
  uploadDate: "2026-10-19",
  width: 1920,
  height: 1080,
  poster: `${MEDIA_BASE}/platform-film-poster.jpg`,
  /* A phone takes the 720 file, half the bytes; a browser that ignores `media`
     takes the first source. */
  sources: [
    { src: `${MEDIA_BASE}/platform-film-1080.mp4`, type: "video/mp4", media: "(min-width: 768px)" },
    { src: `${MEDIA_BASE}/platform-film-720.mp4`, type: "video/mp4" },
  ],
} as const satisfies Film;

/** Core, the operating system: plays by itself on /operating-system. Its
 *  captions are burned into the picture, so it carries no caption track. */
export const CORE_FILM = {
  title: "PEOPLE Core, the operating system",
  description:
    "PEOPLE Core in 90 seconds: residents, care, medications, money and people on one record, and the AI employees who work in it.",
  length: "1:27",
  seconds: 87,
  isoDuration: "PT1M27S",
  uploadDate: "2026-10-18",
  width: 1920,
  height: 1080,
  poster: `${MEDIA_BASE}/core-film-poster.webp`,
  /* It plays without a click, so every visitor downloads it: a phone skips the
     1080 file. A browser that ignores `media` takes the first source. */
  sources: [
    { src: `${MEDIA_BASE}/core-film-1080.mp4`, type: "video/mp4", media: "(min-width: 768px)" },
    { src: `${MEDIA_BASE}/core-film-720.mp4`, type: "video/mp4" },
  ],
} as const satisfies Film;

/** Every file a film serves, as routes/media.$file.ts has to answer for it. */
export function filmPaths(film: Film): string[] {
  return [
    film.poster,
    ...(film.captions ? [film.captions] : []),
    ...film.sources.map((s) => s.src),
  ];
}

/** A media path as the head needs it: absolute on the site's own origin. */
function absolute(path: string): string {
  return /^https?:\/\//.test(path) ? path : `${PEOPLE_ORIGIN}${path}`;
}

export function filmOgVideo(film: Film) {
  return {
    url: absolute(film.sources[0].src),
    type: film.sources[0].type,
    width: film.width,
    height: film.height,
  };
}

/** A film for a page's structured data; `id` is the path and fragment it sits at. */
export function filmVideoObject(film: Film, id: string) {
  return {
    "@type": "VideoObject",
    "@id": `${PEOPLE_ORIGIN}${id}`,
    name: film.title,
    description: film.description,
    thumbnailUrl: absolute(film.poster),
    contentUrl: absolute(film.sources[0].src),
    duration: film.isoDuration,
    uploadDate: film.uploadDate,
    width: film.width,
    height: film.height,
    inLanguage: "en",
    publisher: PUBLISHER,
  };
}

export const FILM_OG_VIDEO = filmOgVideo(FILM);

/** The film, for the home page's structured data. */
export const FILM_VIDEO_OBJECT = filmVideoObject(FILM, "/#film");

export type FilmEvent = "film_open" | "film_play" | "film_75pct" | "film_complete";

interface FilmEventDetail {
  /** Which button opened it: the page and the spot. */
  film_trigger: string;
  video_current_time: number;
  video_percent: number;
}

/** One viewing: where it started, and which milestones it has reported. */
export interface FilmWatch {
  trigger: string;
  reported: Set<FilmEvent>;
}

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

/* Tag Manager carries GA4 and the Meta pixel (__root.tsx): the container decides
   what each event becomes there, so nothing here calls either tag directly. */
export function trackFilm(event: FilmEvent, detail: FilmEventDetail, film: Film = FILM): void {
  if (typeof window === "undefined") return;
  (window.dataLayer ??= []).push({
    event,
    video_title: film.title,
    video_duration: film.seconds,
    video_provider: "self-hosted",
    ...detail,
  });
}

/** Reports a milestone once per viewing, with where the video stands. */
export function reportFilm(
  watch: FilmWatch | null,
  event: FilmEvent,
  video: HTMLVideoElement,
  film: Film = FILM,
): void {
  if (!watch || watch.reported.has(event)) return;
  watch.reported.add(event);
  const duration = Number.isFinite(video.duration) ? video.duration : film.seconds;
  trackFilm(
    event,
    {
      film_trigger: watch.trigger,
      video_current_time: Math.round(video.currentTime),
      video_percent: Math.round((video.currentTime / duration) * 100),
    },
    film,
  );
}
